// 👇 Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
// 👇 Axios Imports
import axios from 'axios'
// 👇️ Endpoints
import { urlOceanBookingService, urlConfigCacheService, urlVesselVoyageService, urlCRMCompanyService, urlFreightForwardService } from '@src/endpoints'
// 👇️ Utils
import { getUriParams } from '@utils'
import toast from 'react-hot-toast'
// 👇 Components
import { ToastContent } from "@src/views/components/toast/ToastContent"

const ToastMessage = (heading, message, isSuccess) => {
  toast(t => (
    <ToastContent t={t} heading={heading} response={isSuccess} message={message} />
  ))
}

//function to remove all null, undefined, empty string from object else make the value caps
function clean(obj) {
  if (!obj) return {}
  for (const propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
      delete obj[propName]
    } else if (Array.isArray(obj[propName]) && obj[propName].length === 0) {
      delete obj[propName]
    } else if (typeof obj[propName] === 'string') {
      obj[propName] = obj[propName].toUpperCase()
    }
  }
  return obj
}

// Get Ocean Booking Status Count
export const getOceanBookingStatusCount = createAsyncThunk(
  "oceanBooking/getOceanBookingStatusCount",
  async () => {
    const response = await axios.get(
      `${urlOceanBookingService}/GetOceanBookingStatusCount`
    )
    return response.data
  }
)

export const getData = createAsyncThunk('oceanBooking/getAllVesselVoyageByTerminal', async (data, { dispatch }) => {
  const { params, filterForm } = data
  const uri = getUriParams(params)

  // Set default values for page and perPage if they are not present in filterForm
  const { ...restFilterForm } = filterForm

  const response = await axios.post(`${urlOceanBookingService}/GetAllOceanBookingsList?${uri}`, {
    ...clean(restFilterForm),
    id: ""

  })

  // Fetch status count after getting data
  dispatch(getOceanBookingStatusCount())

  return {
    params,
    terminal: response.data.data.terminal === '' ? {} : response.data.data.terminal,
    data: response.data.data || [],
    totalPages: response.data.totalCount || 0,
    loaded: true,
    filterForm
  }
})
export const getVoyageID = async data => {
  const response = await axios.post(`${urlVesselVoyageService}/GetAllVoyage?`, { id: "", ...data })
  if (!response.data.success) return {
    success: false, message: 'No Voyage found'
  }
  return { success: true, data: response.data.data[0]?.id }
}
export const getCustomers = createAsyncThunk('oceanBooking/getCustomers', async () => {
  const response = await axios.get(`${urlCRMCompanyService}/GetAllCompanyRolesSelect/CUSTOMER`)
  const regions = await axios.get(`${urlConfigCacheService}/GetAllSelect/RegionCity`)
  const consignee = await axios.get(`${urlConfigCacheService}/Consignee`)
  const containerSizes = await axios.get(`${urlConfigCacheService}?type=ContainerSize`)
  const ff = await axios.get(`${urlCRMCompanyService}/GetAllCompanyTypesSelect/Freight%20Forwarder`)
  const bookingType = await axios.get(`${urlConfigCacheService}/BookingType`)
  const bookingStatus = await axios.get(`${urlConfigCacheService}/BookingStatus`)
  const vesselName = await axios.post(`${urlVesselVoyageService}/GetAllVessel`, { Id: '' }, // Empty data object if no request body is required
    {
      headers: {
        'Content-Type': 'application/json' // Set the Content-Type header
      }
    }
  )
  const containerSizesList = [{ value: '', label: 'Select...' }, ...(containerSizes.data.map(el => ({ value: el.id, label: el.id })))]
  const customers = response.data.map(el => ({ value: `${el.companyCode} - ${el.companyName}`, label: `${el.companyCode} - ${el.companyName}` }))
  const regionsList = [{ value: '', label: 'Select...' }, ...(regions.data.data.map(el => ({ value: el.label, label: el.label })))]
  const consigneeList = [{ value: '', label: 'Select...' }, ...(consignee.data.map(el => ({ value: el.name, label: el.name })))]
  const ffList = [{ value: '', label: 'Select...', isForwarderOnly: '' }, ...(ff.data.map(el => ({ value: `${el.companyCode} - ${el.companyName}`, label: `${el.companyCode} - ${el.companyName}`, isForwarderOnly: el.isForwarderOnly })))]
  const bookingTypeList = [{ value: '', label: 'Select...' }, ...(bookingType.data.map(el => ({ value: el.name, label: el.name })))]
  const statusList = [{ value: '', label: 'Select...' }, ...(bookingStatus.data.map(el => ({ value: el.name, label: el.name })))]
  const vesselList = [{ value: '', label: 'Select...' }, ...(vesselName.data.data.map(el => ({ value: el.name, label: el.name })))]
  return { customers, regions: regionsList, consignee: consigneeList, ff: ffList, bookingType: bookingTypeList, bookingStatus: statusList, vesselName: vesselList, containerSizes: containerSizesList }
})
//get oceanbooking by id
export const getOceanBookingById = createAsyncThunk(
  'oceanBooking/GetOceanBookingsId',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${urlOceanBookingService}/GetOceanBookingsId/${id}`)
      if (!response.data) {
        return rejectWithValue('No data received from GetOceanBookingsId')
      }
      return {
        selectedOceanBooking: response.data,
        loadedOceanBooking: true
      }
    } catch (error) {
      console.error('Error in getOceanBookingById:', error)
      return rejectWithValue(error.response?.data?.message || 'An error occurred while fetching ocean booking data')
    }
  }
)
// /AddOrUpdateOceanBookings -post
// AddOrUpdateOceanBookings - post
export const addOceanBooking = createAsyncThunk(
  'oceanBooking/AddOrUpdateOceanBookings',
  async (data, { dispatch, rejectWithValue }) => {
    const { oceanBookingData, params, filterForm, id } = data
    try {
      // Step 1: Add or update ocean booking
      const response = await axios.post(`${urlOceanBookingService}/AddOrUpdateOceanBookings`, { ...oceanBookingData, id })
       // Check if the API returned success: false
      if (response.data.success === false) {
        const errorMessage = response?.data?.errors?.length > 0 ? response.data.errors.join(', ') : response?.data?.errorDetail || response?.data?.messages || 'An error occurred while processing the booking'
        ToastMessage('Error', errorMessage, false)

        // Return error information using rejectWithValue
        return rejectWithValue({
          errors: response.data.errors || [errorMessage],
          errorDetail: response.data.errorDetail,
          success: false
        })
      }
      // Handle success messages
      if (response.data.success === true) {
        const successMessage = response?.data?.messages?.length > 0 ? response.data.messages.join(', ') : "The Booking Updated Successfully"
        ToastMessage('Success', successMessage, true)
      }
      // Validate response structure
      if (!response.data || !response.data.data || !response.data.data.id) {
        const errorMsg = 'Invalid response from AddOrUpdateOceanBookings'
        ToastMessage('Error', errorMsg, false)
        return rejectWithValue({
          errors: [errorMsg],
          success: false
        })
      }
      const oceanId = response.data.data.id

      // Step 2: Get the updated ocean booking by ID
      const getByIdResponse = await dispatch(getOceanBookingById(oceanId))

      if (!getByIdResponse.payload || !getByIdResponse.payload.selectedOceanBooking) {
        const errorMsg = 'Failed to fetch updated ocean booking data'
        ToastMessage('Error', errorMsg, false)
        return rejectWithValue({
          errors: [errorMsg],
          success: false
        })
      }

      // Step 3: Refresh the data list
      await dispatch(getData({ params, filterForm }))

      return {
        oceanBookingData: getByIdResponse.payload.selectedOceanBooking,
        oceanId,
        success: true
      }
    } catch (error) {
      console.error('Error in addOceanBooking:', error)

      // Handle API errors from axios error response
      if (error.response?.data) {
        const apiData = error.response.data
        const apiErrorMessage = apiData.errors?.length > 0 ? apiData.errors.join(', ') : apiData.errorDetail || 'API returned an error'
        ToastMessage('Error', apiErrorMessage, false)
        return rejectWithValue({
          errors: apiData.errors || [apiErrorMessage],
          errorDetail: apiData.errorDetail,
          success: false
        })
      }

      // Handle network or other errors
      const errorMessage = error.message || 'An unexpected error occurred'
      ToastMessage('Error', errorMessage, false)

      return rejectWithValue({
        errors: [errorMessage],
        success: false
      })
    }
  }
)
//api call for adding FF Order
export const addFFRequest = createAsyncThunk('ffRequests/addFFRequest', async (FFRequestViewModel, { dispatch, getState }) => {
  const response = await axios.post(`${urlFreightForwardService}/AddFFRequest`, FFRequestViewModel)

  await dispatch(getData(getState().freightForward.params))
  await dispatch(getAllData())
  //await dispatch(getFFResponse(response))
  return {
    ffResponse: response
  }
})

//api call to update bulk vesselVoyage pairs in multiple rows

export const updateBulkVesselVoyage = createAsyncThunk('oceanBooking/UpdateBookingVesselCutOff', async (data, { dispatch }) => {
  const { updatedVesselData, params, filterForm } = data
  await axios.post(`${urlOceanBookingService}/UpdateBookingVesselCutOff`, {
    ...updatedVesselData,
    id: ''
  })
  await dispatch(getData({ params, filterForm }))
  return updatedVesselData
})


// /SubmitLateGate - post
// Submits a Late Gate acceptance for a whole booking (empty containerNumbers)
// or for a specific list of containers under the booking.
export const submitLateGate = createAsyncThunk('oceanBooking/SubmitLateGate', async (data, { dispatch, getState, rejectWithValue }) => {
  const { bookingNumber, lateGateDateTime, containerNumbers, id } = data
  try {
    const response = await axios.post(`${urlOceanBookingService}/SubmitLateGate`, {
      bookingNumber,
      lateGateDateTime,
      containerNumbers: containerNumbers || []
    })

    if (response.data.success === true) {
      ToastMessage('Success', response?.data?.messages?.length > 0 ? response.data.messages.join(', ') : 'Late Gate accepted.', true)

      // Refresh the detail view (if we know the record id) and the list.
      if (id) {
        await dispatch(getOceanBookingById(id))
      }
      const { params, filterForm } = getState().oceanBooking
      await dispatch(getData({ params, filterForm }))

      return { bookingNumber, success: true }
    } else {
      const errorMessage = response?.data?.errors?.length > 0 ? response.data.errors.join(', ') : 'Failed to submit Late Gate.'
      ToastMessage('Error', errorMessage, false)
      return rejectWithValue({ errors: response.data.errors || [errorMessage] })
    }
  } catch (error) {
    const errorMessage = error.response?.data?.errors?.[0] || error.response?.data?.errorDetail || error.message || 'An error occurred while submitting Late Gate'
    ToastMessage('Error', errorMessage, false)
    return rejectWithValue({ errors: [errorMessage] })
  }
})

// /ActivateOceanBookings -patch
export const activateVessel = createAsyncThunk('oceanBooking/ActivateOceanBookings', async (data, { dispatch }) => {
  const { id, params, filterForm } = data
  await axios.patch(`${urlOceanBookingService}/ActivateOceanBookings?id=${id}`)
  await dispatch(getData({ params, filterForm }))
  return id
})

// /DeactivateOceanBookings -patch
export const deactivateVessel = createAsyncThunk('oceanBooking/DeactivateOceanBookings', async (data, { dispatch }) => {
  const { id, params, filterForm } = data
  await axios.patch(`${urlOceanBookingService}/DeactivateOceanBookings?id=${id}`)
  await dispatch(getData({ params, filterForm }))
  return id
})

// /DeleteOceanBookings -delete
export const deleteVessel = createAsyncThunk('oceanBooking/deleteVessel', async (id, { dispatch, getState }) => {
  const response = await axios.delete(`${urlOceanBookingService}/DeleteOceanBookings/${id}`)
  response.data.success === true ? ToastMessage('Success', response.data.messages[0], true) : ToastMessage('Error', response.data.errors[0], false)
  // Fetch the current state of filterForm along with params
  const { params, filterForm } = getState().oceanBooking
  await dispatch(getData({ params, filterForm }))
  return id
})

// UpdateLifecycleStatusOceanBookingById - PATCH
export const updateLifecycleStatus = createAsyncThunk('oceanBooking/updateLifecycleStatus', async (data, { dispatch, getState, rejectWithValue }) => {
  const { id, action } = data
  try {
    const response = await axios.patch(`${urlOceanBookingService}/UpdateLifecycleStatusOceanBookingById?id=${id}&action=${action}`)

    // Handle response - display backend messages
    if (response.data.success === true) {
      ToastMessage('Success', response.data.messages[0], true)

      // Fetch the current state of filterForm along with params
      const { params, filterForm } = getState().oceanBooking
      await dispatch(getData({ params, filterForm }))

      return { id, action, success: true }
    } else {
      ToastMessage('Error', response.data.errors[0], false)
      return rejectWithValue({ errors: response.data.errors })
    }
  } catch (error) {
    const errorMessage = error.response?.data?.errors?.[0] || error.response?.data?.errorDetail || error.message || 'An error occurred while updating booking status'
    ToastMessage('Error', errorMessage, false)
    return rejectWithValue({ errors: [errorMessage] })
  }
})

export const oceanBookingSlice = createSlice({
  name: 'oceanBooking',
  initialState: {
    loaded: false,
    loadedAll: false,
    loadedFFR: false,
    data: [],
    terminal: {},
    total: 1,
    params: {},
    filterForm: {},
    allData: {},
    status: [],
    notes: [],
    allTimeSheetUser: [],
    allContacts: [],
    customers: [],
    consignee: [],
    freightForwarder: [],
    regions: [],
    bookingType: [],
    bookingStatus: [],
    vesselName: [],
    containerSizes: [],
    selectedOceanBooking: null,
    loadedOceanBooking: false,
    oceanId: "",
    createdEmployee: "",
    statusCount: {
      pendingReview: 0,
      requested: 0,
      assigned: 0,
      loading: 0,
      completed: 0,
      delivered: 0,
      cancelled: 0,
      invalid: 0
    }

  },

  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getData.fulfilled, (state, action) => {
        state.loaded = action.payload.loaded
        state.data = action.payload.data
        state.params = action.payload.params
        state.total = action.payload.totalPages
        state.terminal = action.payload.terminal
        state.filterForm = action.payload.filterForm

      })
      .addCase(addOceanBooking.fulfilled, (state, action) => {
        state.selectedOceanBooking = action?.payload?.oceanBookingData
        state.oceanId = action?.payload?.oceanBookingData?.id
        state.createdEmployee = action?.payload?.oceanBookingData?.createdBy
      })
      .addCase(getData.rejected, (state) => {
        state.loaded = true
        state.data = []
        state.params = {}
        state.total = 0
      })
      .addCase(getOceanBookingById.fulfilled, (state, action) => {
        state.selectedOceanBooking = action.payload.selectedOceanBooking
        state.loadedOceanBooking = action.payload.loadedOceanBooking
      })

      .addCase(getCustomers.fulfilled, (state, action) => {
        state.customers = action.payload.customers
        state.consignee = action.payload.consignee
        state.freightForwarder = action.payload.ff
        state.containerSizes = action.payload.containerSizes
        state.regions = action.payload.regions
        state.bookingType = action.payload.bookingType
        state.bookingStatus = action.payload.bookingStatus
        state.vesselName = action.payload.vesselName
      })
      .addCase(getOceanBookingStatusCount.fulfilled, (state, action) => {
        state.statusCount = action.payload
      })
      //anything fulfilled will set loaded to true
      .addMatcher(action => action.type.endsWith('/fulfilled'), (state) => {
        state.loaded = true
      })
      //anything pending will set loaded to false
      .addMatcher(action => action.type.endsWith('/pending'), (state) => {
        state.loaded = false
      })
      //reset loaded to true if rejected
      .addMatcher(action => action.type.endsWith('/rejected'), (state) => {
        state.loaded = true
      })

  }
})

export default oceanBookingSlice.reducer